import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function CEEOfferSection() {
  const features = [
    {
      icon: 'ri-money-euro-circle-line',
      title: 'Installation dès 1€',
      description: 'Profitez du financement CEE pour rénover votre éclairage sans investissement initial',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Dossier CEE géré',
      description: 'Nous nous occupons de toutes les démarches administratives pour vous',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Certifié BAT-EQ-127',
      description: 'Nos installations respectent toutes les normes CEE en vigueur',
    },
    {
      icon: 'ri-calculator-line',
      title: 'Simulation gratuite',
      description: 'Calculez vos économies et votre éligibilité ',
    },
  ];

  return (
    <section className="relative py-4 sm:py-8 lg:py-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(16, 185, 129, 0.4) 1px, transparent 0)`,
          backgroundSize: '48px 48px'
        }}></div>
      </div>

      {/* Gradient Orbs - Desktop only animations */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={window.innerWidth >= 768 ? {
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.3, 0.1],
          } : {}}
          transition={window.innerWidth >= 768 ? { duration: 10, repeat: Infinity, ease: "easeInOut" } : {}}
          className="absolute top-1/2 left-0 w-32 sm:w-48 lg:w-64 h-32 sm:h-48 lg:h-64 bg-emerald-500/20 rounded-full blur-2xl"
        ></motion.div>
        <motion.div
          animate={window.innerWidth >= 768 ? {
            scale: [1.3, 1, 1.3],
            opacity: [0.1, 0.2, 0.1],
          } : {}}
          transition={window.innerWidth >= 768 ? { duration: 12, repeat: Infinity, ease: "easeInOut" } : {}}
          className="absolute top-1/2 right-0 w-32 sm:w-48 lg:w-64 h-32 sm:h-48 lg:h-64 bg-teal-500/15 rounded-full blur-2xl"
        ></motion.div>
      </div>

      <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 items-center">
          {/* Left Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-1 sm:gap-1.5 bg-emerald-500/20 backdrop-blur-sm border border-emerald-400/30 rounded-full px-2 sm:px-3 py-1 sm:py-1.5 mb-2 sm:mb-3 lg:mb-4"
            >
              <span className={`w-1 h-1 sm:w-1.5 sm:h-1.5 bg-emerald-400 rounded-full ${window.innerWidth >= 768 ? 'animate-pulse' : ''}`}></span>
              <span className="text-[10px] sm:text-xs font-bold text-emerald-400 tracking-wide uppercase">Offre CEE</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 sm:mb-3 lg:mb-4 leading-tight"
            >
              Rénovez Votre Éclairage
              <br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                Pour Seulement 1€
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xs sm:text-sm lg:text-base text-gray-300 mb-3 sm:mb-4 lg:mb-5 leading-relaxed"
            >
              Grâce au dispositif des Certificats d'Économies d'Énergie (CEE), bénéficiez d'une installation LED professionnelle avec un reste à charge minimal.
            </motion.p>
          </div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            {/* Main Card */}
            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-xl p-3 sm:p-4 lg:p-6">
              {/* Price Display */}
              <div className="text-center mb-3 sm:mb-4 lg:mb-5">
                <div className="text-gray-400 text-[10px] sm:text-xs mb-0.5">Reste à charge</div>
                <div className="flex items-center justify-center gap-1 sm:gap-2 mb-0.5">
                  <span className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white">1€</span>
                </div>
                <div className="text-gray-300 text-[9px] sm:text-xs">Grâce au financement CEE</div>
              </div>

              {/* Benefits */}
              <div className="space-y-1 sm:space-y-1.5">
                {[
                  'Installation complète incluse',
                  'Matériel LED haute performance',
                  'Garantie 5 ans pièces et main d\'œuvre',
                  'Étude photométrique offerte',
                  'Dossier CEE géré de A à Z',
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-1.5 sm:gap-2 text-white">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-[8px] sm:text-xs"></i>
                    </div>
                    <span className="text-[10px] sm:text-xs font-medium">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Decorative Elements - Static on mobile */}
              <div className="absolute -top-2 -right-2 w-10 sm:w-12 lg:w-16 h-10 sm:h-12 lg:h-16 bg-gradient-to-br from-emerald-500/30 to-teal-500/30 rounded-full blur-xl"></div>
              <div className="absolute -bottom-2 -left-2 w-10 sm:w-12 lg:w-16 h-10 sm:h-12 lg:h-16 bg-gradient-to-br from-teal-500/30 to-cyan-500/30 rounded-full blur-xl"></div>
            </div>

            {/* Floating Badge - Desktop only animations */}
            <motion.div
              animate={window.innerWidth >= 768 ? {
                y: [0, -6, 0],
              } : {}}
              transition={window.innerWidth >= 768 ? { duration: 3, repeat: Infinity, ease: "easeInOut" } : {}}
              className="absolute -top-2 -left-2 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 shadow-xl"
            >
              <div className="text-[9px] sm:text-[10px] font-semibold mb-0.5">Économies moyennes</div>
              <div className="text-base sm:text-lg font-bold">75%</div>
            </motion.div>
          </motion.div>
        </div>

        {/* CTA Button mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="lg:hidden mt-4 text-center"
        >
          <Link
            to="/etude-gratuite"
            className="group inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-bold hover:shadow-xl hover:shadow-emerald-500/50 hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
          >
            Vérifier mon éligibilité
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform text-xs"></i>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
